import React, { useState, useEffect } from 'react';
import { Search, MapPin, Calendar, Users, Phone, Mail, Plus, Edit, Trash2, DollarSign, Camera, Home, Package, User, CreditCard, BarChart3, Download, Send, Menu, X } from 'lucide-react';

// Types
interface Tour {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: string;
  location: string;
  images: string[];
  highlights: string[];
  inclusions: string[];
  exclusions: string[];
}

interface Destination {
  id: string;
  name: string;
  location: string;
  description: string;
  history: string;
  images: string[];
  bestTimeToVisit: string;
  nearbyAttractions: string[];
}

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  tourId: string;
  tourName: string;
  totalAmount: number;
  paidAmount: number;
  remainingBalance: number;
  bookingDate: string;
  payments: Payment[];
}

interface Payment {
  id: string;
  customerId: string;
  amount: number;
  date: string;
  method: string;
  receiptUrl: string;
}

interface BookingForm {
  name: string;
  email: string;
  phone: string;
  tourName: string;
  numberOfPeople: number;
}

// Initial dummy data with working images
const initialTours: Tour[] = [
  {
    id: '1',
    name: 'Golden Triangle with Varanasi',
    description: 'Experience the spiritual heart of India with visits to Delhi, Agra, Jaipur, and the holy city of Varanasi.',
    price: 25000,
    duration: '8 Days / 7 Nights',
    location: 'Delhi - Agra - Jaipur - Varanasi',
    images: [
      'https://images.unsplash.com/photo-1548013146-72479768bada?w=800&q=80',
      'https://images.unsplash.com/photo-1532664189809-02133fee698d?w=800&q=80',
      'https://images.unsplash.com/photo-1463592177119-bab2a00ba3d5?w=800&q=80',
      'https://images.unsplash.com/photo-1539650116574-75c0c6d8e8b8?w=800&q=80'
    ],
    highlights: ['Taj Mahal at sunrise', 'Ganga Aarti in Varanasi', 'Red Fort Delhi', 'Hawa Mahal Jaipur'],
    inclusions: ['Accommodation', 'Meals', 'Transportation', 'Guide'],
    exclusions: ['Airfare', 'Personal expenses', 'Tips']
  },
  {
    id: '2',
    name: 'Char Dham Yatra',
    description: 'Sacred journey to the four holy shrines of Yamunotri, Gangotri, Kedarnath, and Badrinath.',
    price: 35000,
    duration: '12 Days / 11 Nights',
    location: 'Uttarakhand',
    images: [
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      'https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=800&q=80',
      'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?w=800&q=80',
      'https://images.unsplash.com/photo-1591123720164-6eea2dcb30c5?w=800&q=80'
    ],
    highlights: ['Kedarnath Temple', 'Badrinath Darshan', 'Gangotri Glacier', 'Yamunotri Hot Springs'],
    inclusions: ['Accommodation', 'Meals', 'Transportation', 'Helicopter service'],
    exclusions: ['Personal expenses', 'Tips', 'Emergency medical']
  },
  {
    id: '3',
    name: 'South India Temple Tour',
    description: 'Explore the magnificent temples of Tamil Nadu, Karnataka, and Kerala.',
    price: 28000,
    duration: '10 Days / 9 Nights',
    location: 'Chennai - Madurai - Kanyakumari - Kochi',
    images: [
      'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=800&q=80',
      'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=800&q=80',
      'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&q=80',
      'https://images.unsplash.com/photo-1589190542530-910f80b5ac9f?w=800&q=80'
    ],
    highlights: ['Meenakshi Temple', 'Kanyakumari Sunset', 'Backwaters of Kerala', 'Kapaleeshwarar Temple'],
    inclusions: ['Accommodation', 'Meals', 'Transportation', 'Guide'],
    exclusions: ['Airfare', 'Personal expenses', 'Tips']
  },
  {
    id: '4',
    name: 'Rajasthan Heritage Tour',
    description: 'Discover the royal heritage of Rajasthan with visits to majestic palaces and ancient temples.',
    price: 32000,
    duration: '9 Days / 8 Nights',
    location: 'Jaipur - Udaipur - Jodhpur - Jaisalmer',
    images: [
      'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=800&q=80',
      'https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=800&q=80',
      'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=800&q=80',
      'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?w=800&q=80'
    ],
    highlights: ['City Palace Udaipur', 'Mehrangarh Fort', 'Dilwara Temples', 'Camel Safari Jaisalmer'],
    inclusions: ['Accommodation', 'Meals', 'Transportation', 'Local Guide'],
    exclusions: ['Airfare', 'Personal expenses', 'Tips', 'Camel safari fees']
  },
  {
    id: '5',
    name: 'Haridwar Rishikesh Spiritual Tour',
    description: 'Immerse yourself in spirituality with yoga, meditation, and sacred rituals by the holy Ganges.',
    price: 18000,
    duration: '5 Days / 4 Nights',
    location: 'Haridwar - Rishikesh',
    images: [
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80',
      'https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=800&q=80',
      'https://images.unsplash.com/photo-1591123720164-6eea2dcb30c5?w=800&q=80'
    ],
    highlights: ['Ganga Aarti at Har Ki Pauri', 'Yoga sessions in Rishikesh', 'Lakshman Jhula', 'Beatles Ashram'],
    inclusions: ['Accommodation', 'Meals', 'Transportation', 'Yoga sessions'],
    exclusions: ['Airfare', 'Personal expenses', 'Adventure activities']
  },
  {
    id: '6',
    name: 'Amritsar Golden Temple Tour',
    description: 'Experience the divine beauty of the Golden Temple and learn about Sikh heritage and culture.',
    price: 22000,
    duration: '4 Days / 3 Nights',
    location: 'Amritsar - Chandigarh',
    images: [
      'https://images.unsplash.com/photo-1609920658906-8223bd289001?w=800&q=80',
      'https://images.unsplash.com/photo-1586648645637-71d2b0c65d30?w=800&q=80',
      'https://images.unsplash.com/photo-1624969862293-b749659ccc2e?w=800&q=80',
      'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&q=80'
    ],
    highlights: ['Golden Temple Amritsar', 'Wagah Border Ceremony', 'Jallianwala Bagh', 'Rock Garden Chandigarh'],
    inclusions: ['Accommodation', 'Meals', 'Transportation', 'Entry fees'],
    exclusions: ['Airfare', 'Personal expenses', 'Shopping']
  }
];

const initialDestinations: Destination[] = [
  {
    id: '1',
    name: 'Meenakshi Temple',
    location: 'Madurai, Tamil Nadu',
    description: 'One of India\'s most magnificent temple complexes dedicated to Goddess Meenakshi.',
    history: 'The Meenakshi Temple is a historic Hindu temple complex located in Madurai, Tamil Nadu. Built between the 12th and 18th centuries, this temple is dedicated to Meenakshi, a form of Parvati, and her consort, Sundareshwarar, a form of Shiva. The temple complex covers 14 acres and contains 14 towers, ranging from 45-50m in height.',
    images: [
      'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600&q=80',
      'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&q=80'
    ],
    bestTimeToVisit: 'October to March',
    nearbyAttractions: ['Thirumalai Nayakkar Palace', 'Gandhi Memorial Museum', 'Alagar Koil']
  },
  {
    id: '2',
    name: 'Kedarnath Temple',
    location: 'Uttarakhand',
    description: 'One of the twelve Jyotirlingas and highest among all Shiva temples.',
    history: 'Kedarnath Temple is one of the holiest Hindu temples dedicated to Lord Shiva, located at an altitude of 3,583 meters in the Garhwal Himalayas. Built in the 8th century CE by Adi Shankaracharya, this temple is one of the twelve Jyotirlingas and part of the smaller Char Dham pilgrimage circuit.',
    images: [
      'https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=600&q=80',
      'https://images.unsplash.com/photo-1626897505254-e0f811aa9bf7?w=600&q=80'
    ],
    bestTimeToVisit: 'May to October',
    nearbyAttractions: ['Chorabari Tal', 'Vasuki Tal', 'Mandakini River']
  },
  {
    id: '3',
    name: 'Taj Mahal',
    location: 'Agra, Uttar Pradesh',
    description: 'The epitome of love and one of the Seven Wonders of the World.',
    history: 'The Taj Mahal is an ivory-white marble mausoleum commissioned in 1632 by Mughal emperor Shah Jahan to house the tomb of his favorite wife, Mumtaz Mahal. Construction took 22 years and employed 20,000 artisans under the guidance of architect Ustad Ahmad Lahauri.',
    images: [
      'https://images.unsplash.com/photo-1564507592333-c60657eea523?w=600&q=80',
      'https://images.unsplash.com/photo-1587135941948-670b381f08ce?w=600&q=80'
    ],
    bestTimeToVisit: 'October to March',
    nearbyAttractions: ['Agra Fort', 'Fatehpur Sikri', 'Itimad-ud-Daulah']
  },
  {
    id: '4',
    name: 'Golden Temple',
    location: 'Amritsar, Punjab',
    description: 'The holiest shrine of Sikhism, known for its stunning golden architecture.',
    history: 'The Golden Temple, officially known as Harmandir Sahib, was built in the 16th century by Guru Arjan Dev. The temple sits on a rectangular platform in the middle of a sacred pool called Amrit Sarovar. The upper floors are covered with gold foil, giving it the famous golden appearance.',
    images: [
      'https://images.unsplash.com/photo-1609920658906-8223bd289001?w=600&q=80',
      'https://images.unsplash.com/photo-1586648645637-71d2b0c65d30?w=600&q=80'
    ],
    bestTimeToVisit: 'November to March',
    nearbyAttractions: ['Jallianwala Bagh', 'Wagah Border', 'Akal Takht']
  },
  {
    id: '5',
    name: 'Varanasi Ghats',
    location: 'Varanasi, Uttar Pradesh',
    description: 'The spiritual capital of India with ancient ghats along the sacred Ganges.',
    history: 'Varanasi, one of the oldest continuously inhabited cities in the world, has been a center of learning and pilgrimage for over 3000 years. The ghats (steps leading to the river) are integral to the spiritual life of the city, with the most famous being Dashashwamedh Ghat where the evening Ganga Aarti takes place.',
    images: [
      'https://images.unsplash.com/photo-1570366583862-f91883984fde?w=600&q=80',
      'https://images.unsplash.com/photo-1596845803716-67b2b8ffaec3?w=600&q=80'
    ],
    bestTimeToVisit: 'October to March',
    nearbyAttractions: ['Kashi Vishwanath Temple', 'Sankat Mochan Temple', 'Bharat Mata Temple']
  },
  {
    id: '6',
    name: 'Hampi Temples',
    location: 'Karnataka',
    description: 'UNESCO World Heritage site with magnificent ruins of the Vijayanagara Empire.',
    history: 'Hampi was the capital of the Vijayanagara Empire from 1336 to 1565 CE. The site contains more than 1,600 surviving remains including forts, temples, palaces, and other structures. The Virupaksha Temple, dedicated to Lord Shiva, is the main center of pilgrimage and has been operating continuously since the 7th century.',
    images: [
      'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600&q=80',
      'https://images.unsplash.com/photo-1518161012917-7cb6ac1654e4?w=600&q=80'
    ],
    bestTimeToVisit: 'October to February',
    nearbyAttractions: ['Virupaksha Temple', 'Vittala Temple', 'Elephant Stables']
  }
];

const initialCustomers: Customer[] = [
  {
    id: '1',
    name: 'Rajesh Kumar',
    email: 'rajesh@gmail.com',
    phone: '+91 9876543210',
    address: '123 MG Road, Delhi',
    tourId: '1',
    tourName: 'Golden Triangle with Varanasi',
    totalAmount: 25000,
    paidAmount: 15000,
    remainingBalance: 10000,
    bookingDate: '2024-06-15',
    payments: [
      { id: '1', customerId: '1', amount: 10000, date: '2024-06-15', method: 'UPI', receiptUrl: 'receipt1.pdf' },
      { id: '2', customerId: '1', amount: 5000, date: '2024-06-25', method: 'Cash', receiptUrl: 'receipt2.pdf' }
    ]
  },
  {
    id: '2',
    name: 'Priya Sharma',
    email: 'priya@gmail.com',
    phone: '+91 9876543211',
    address: '456 Park Street, Mumbai',
    tourId: '2',
    tourName: 'Char Dham Yatra',
    totalAmount: 35000,
    paidAmount: 20000,
    remainingBalance: 15000,
    bookingDate: '2024-06-20',
    payments: [
      { id: '3', customerId: '2', amount: 15000, date: '2024-06-20', method: 'Bank Transfer', receiptUrl: 'receipt3.pdf' },
      { id: '4', customerId: '2', amount: 5000, date: '2024-07-01', method: 'UPI', receiptUrl: 'receipt4.pdf' }
    ]
  }
];

const BidaweTravel = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [tours, setTours] = useState<Tour[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedTour, setSelectedTour] = useState<Tour | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddTour, setShowAddTour] = useState(false);
  const [showAddDestination, setShowAddDestination] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingTour, setEditingTour] = useState<Tour | null>(null);
  const [editingDestination, setEditingDestination] = useState<Destination | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingFormData, setBookingFormData] = useState<BookingForm>({
    name: '',
    email: '',
    phone: '',
    tourName: '',
    numberOfPeople: 1
  });

  // Initialize data from localStorage
  useEffect(() => {
    const savedTours = localStorage.getItem('bidave-tours');
    const savedDestinations = localStorage.getItem('bidave-destinations');
    const savedCustomers = localStorage.getItem('bidave-customers');
    const savedLogin = localStorage.getItem('bidave-login');
    
    setTours(savedTours ? JSON.parse(savedTours) : initialTours);
    setDestinations(savedDestinations ? JSON.parse(savedDestinations) : initialDestinations);
    setCustomers(savedCustomers ? JSON.parse(savedCustomers) : initialCustomers);
    setIsLoggedIn(savedLogin === 'true');
  }, []);

  // Save login state
  useEffect(() => {
    localStorage.setItem('bidave-login', isLoggedIn.toString());
  }, [isLoggedIn]);

  // Save data to localStorage
  useEffect(() => {
    localStorage.setItem('bidave-tours', JSON.stringify(tours));
  }, [tours]);

  useEffect(() => {
    localStorage.setItem('bidave-destinations', JSON.stringify(destinations));
  }, [destinations]);

  useEffect(() => {
    localStorage.setItem('bidave-customers', JSON.stringify(customers));
  }, [customers]);

  // Close mobile menu when page changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentPage]);

  // Calculate totals
  const totalOutstanding = customers.reduce((sum, customer) => sum + customer.remainingBalance, 0);
  const totalCollected = customers.reduce((sum, customer) => sum + customer.paidAmount, 0);

  // Login function
  const handleLogin = () => {
    if (loginData.username === 'admin' && loginData.password === 'admin123') {
      setIsLoggedIn(true);
      setCurrentPage('admin');
      setLoginData({ username: '', password: '' });
    } else {
      alert('Invalid credentials. Use: admin / admin123');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentPage('home');
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.phone.includes(searchQuery)
  );

  // Tour management functions
  const addTour = (tourData: Omit<Tour, 'id'>) => {
    const newTour: Tour = {
      ...tourData,
      id: Date.now().toString()
    };
    const updatedTours = [...tours, newTour];
    setTours(updatedTours);
    setShowAddTour(false);
    localStorage.setItem('bidave-tours', JSON.stringify(updatedTours));
  };

  const updateTour = (id: string, tourData: Omit<Tour, 'id'>) => {
    const updatedTours = tours.map(tour => tour.id === id ? { ...tourData, id } : tour);
    setTours(updatedTours);
    setEditingTour(null);
    localStorage.setItem('bidave-tours', JSON.stringify(updatedTours));
  };

  const deleteTour = (id: string) => {
    const updatedTours = tours.filter(tour => tour.id !== id);
    setTours(updatedTours);
    localStorage.setItem('bidave-tours', JSON.stringify(updatedTours));
  };

  // Destination management functions
  const addDestination = (destinationData: Omit<Destination, 'id'>) => {
    const newDestination: Destination = {
      ...destinationData,
      id: Date.now().toString()
    };
    const updatedDestinations = [...destinations, newDestination];
    setDestinations(updatedDestinations);
    setShowAddDestination(false);
    localStorage.setItem('bidave-destinations', JSON.stringify(updatedDestinations));
  };

  const updateDestination = (id: string, destinationData: Omit<Destination, 'id'>) => {
    const updatedDestinations = destinations.map(destination => 
      destination.id === id ? { ...destinationData, id } : destination
    );
    setDestinations(updatedDestinations);
    setEditingDestination(null);
    localStorage.setItem('bidave-destinations', JSON.stringify(updatedDestinations));
  };

  const deleteDestination = (id: string) => {
    const updatedDestinations = destinations.filter(destination => destination.id !== id);
    setDestinations(updatedDestinations);
    localStorage.setItem('bidave-destinations', JSON.stringify(updatedDestinations));
  };

  // Booking form submission
  const submitBookingForm = async () => {
    try {
      const webhookData = {
        ...bookingFormData,
        timestamp: new Date().toISOString(),
        source: 'bidaveyatra.com'
      };

      console.log('Booking form submitted to webhook:', webhookData);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert('Booking request submitted successfully! We will contact you soon.');
      
      setBookingFormData({
        name: '',
        email: '',
        phone: '',
        tourName: '',
        numberOfPeople: 1
      });
      setShowBookingForm(false);
    } catch (error) {
      alert('Error submitting booking form. Please try again.');
    }
  };

  // Customer management functions
  const addCustomer = (customerData: Omit<Customer, 'id' | 'remainingBalance' | 'payments'>) => {
    const newCustomer: Customer = {
      ...customerData,
      id: Date.now().toString(),
      remainingBalance: customerData.totalAmount - customerData.paidAmount,
      payments: customerData.paidAmount > 0 ? [{
        id: Date.now().toString(),
        customerId: Date.now().toString(),
        amount: customerData.paidAmount,
        date: new Date().toISOString().split('T')[0],
        method: 'Cash',
        receiptUrl: 'receipt_' + Date.now() + '.pdf'
      }] : []
    };
    const updatedCustomers = [...customers, newCustomer];
    setCustomers(updatedCustomers);
    setShowAddCustomer(false);
    localStorage.setItem('bidave-customers', JSON.stringify(updatedCustomers));
  };

  const updateCustomer = (id: string, customerData: Partial<Customer>) => {
    const updatedCustomers = customers.map(customer => 
      customer.id === id 
        ? { ...customer, ...customerData, remainingBalance: (customerData.totalAmount || customer.totalAmount) - (customerData.paidAmount || customer.paidAmount) }
        : customer
    );
    setCustomers(updatedCustomers);
    setEditingCustomer(null);
    localStorage.setItem('bidave-customers', JSON.stringify(updatedCustomers));
  };

  // Payment management
  const addPayment = (customerId: string, amount: number, method: string) => {
    const payment: Payment = {
      id: Date.now().toString(),
      customerId,
      amount,
      date: new Date().toISOString().split('T')[0],
      method,
      receiptUrl: `receipt_${Date.now()}.pdf`
    };

    const updatedCustomers = customers.map(customer => {
      if (customer.id === customerId) {
        const newPaidAmount = customer.paidAmount + amount;
        return {
          ...customer,
          paidAmount: newPaidAmount,
          remainingBalance: customer.totalAmount - newPaidAmount,
          payments: [...customer.payments, payment]
        };
      }
      return customer;
    });

    setCustomers(updatedCustomers);
    localStorage.setItem('bidave-customers', JSON.stringify(updatedCustomers));

    console.log('Webhook called for WhatsApp notification:', {
      customerId,
      customerName: customers.find(c => c.id === customerId)?.name,
      amount,
      receiptUrl: payment.receiptUrl,
      remainingBalance: (customers.find(c => c.id === customerId)?.totalAmount || 0) - (customers.find(c => c.id === customerId)?.paidAmount || 0) - amount
    });

    setShowAddPayment(false);
    setSelectedCustomer(null);
  };

  // PDF Download function
  const downloadReceipt = (payment: Payment, customer: Customer) => {
    const receiptContent = `
BIDAVE YATRA
Payment Receipt

Date: ${payment.date}
Receipt No: ${payment.id}

Customer Details:
Name: ${customer.name}
Phone: ${customer.phone}
Email: ${customer.email}

Tour Details:
Tour: ${customer.tourName}
Total Amount: ₹${customer.totalAmount.toLocaleString()}

Payment Details:
Amount Paid: ₹${payment.amount.toLocaleString()}
Payment Method: ${payment.method}
Total Paid: ₹${customer.paidAmount.toLocaleString()}
Remaining Balance: ₹${customer.remainingBalance.toLocaleString()}

Thank you for choosing Bidave Yatra!
Contact: +91 9876543210
Email: info@bidaveyatra.com
Website: https://bidaveyatra.com`;

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receipt_${customer.name}_${payment.date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Mobile Navigation component
  const Navigation = () => (
    <nav className="bg-blue-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
            <span className="text-lg sm:text-2xl font-bold">Bidave Yatra</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-4">
            <button
              onClick={() => setCurrentPage('home')}
              className={`flex items-center space-x-2 px-3 py-2 rounded text-sm font-medium ${
                currentPage === 'home' ? 'bg-blue-700' : 'hover:bg-blue-800'
              }`}
            >
              <Home className="h-4 w-4" />
              <span>Home</span>
            </button>
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => setCurrentPage('admin')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded text-sm font-medium ${
                    currentPage === 'admin' ? 'bg-blue-700' : 'hover:bg-blue-800'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" />
                  <span>Admin</span>
                </button>
                <button
                  onClick={() => setCurrentPage('customers')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded text-sm font-medium ${
                    currentPage === 'customers' ? 'bg-blue-700' : 'hover:bg-blue-800'
                  }`}
                >
                  <User className="h-4 w-4" />
                  <span>Customers</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-3 py-2 rounded text-sm font-medium hover:bg-red-600"
                >
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => setCurrentPage('login')}
                className={`flex items-center space-x-2 px-3 py-2 rounded text-sm font-medium ${
                  currentPage === 'login' ? 'bg-blue-700' : 'hover:bg-blue-800'
                }`}
              >
                <User className="h-4 w-4" />
                <span>Login</span>
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-white"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-blue-800">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button
                onClick={() => setCurrentPage('home')}
                className={`flex items-center space-x-2 w-full px-3 py-2 rounded text-sm font-medium ${
                  currentPage === 'home' ? 'bg-blue-700' : 'hover:bg-blue-800'
                }`}
              >
                <Home className="h-4 w-4" />
                <span>Home</span>
              </button>
              {isLoggedIn ? (
                <>
                  <button
                    onClick={() => setCurrentPage('admin')}
                    className={`flex items-center space-x-2 w-full px-3 py-2 rounded text-sm font-medium ${
                      currentPage === 'admin' ? 'bg-blue-700' : 'hover:bg-blue-800'
                    }`}
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Admin Dashboard</span>
                  </button>
                  <button
                    onClick={() => setCurrentPage('customers')}
                    className={`flex items-center space-x-2 w-full px-3 py-2 rounded text-sm font-medium ${
                      currentPage === 'customers' ? 'bg-blue-700' : 'hover:bg-blue-800'
                    }`}
                  >
                    <User className="h-4 w-4" />
                    <span>Customer Management</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 w-full px-3 py-2 rounded text-sm font-medium hover:bg-red-600"
                  >
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setCurrentPage('login')}
                  className={`flex items-center space-x-2 w-full px-3 py-2 rounded text-sm font-medium ${
                    currentPage === 'login' ? 'bg-blue-700' : 'hover:bg-blue-800'
                  }`}
                >
                  <User className="h-4 w-4" />
                  <span>Admin Login</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );

  // Login Page
  const LoginPage = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg max-w-md w-full">
        <h2 className="text-2xl font-bold text-center mb-6">Admin Login</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Username</label>
            <input
              type="text"
              value={loginData.username}
              onChange={(e) => setLoginData({...loginData, username: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <input
              type="password"
              value={loginData.password}
              onChange={(e) => setLoginData({...loginData, password: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter password"
            />
          </div>
          <button
            onClick={handleLogin}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
          >
            Login
          </button>
        </div>
        <div className="mt-4 text-center text-sm text-gray-600">
          <p>Demo credentials:</p>
          <p>Username: <strong>admin</strong></p>
          <p>Password: <strong>admin123</strong></p>
        </div>
      </div>
    </div>
  );

  // Home page component
  const HomePage = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-12 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">Discover Sacred India</h1>
          <p className="text-lg sm:text-xl mb-6 sm:mb-8 max-w-3xl mx-auto">Your spiritual journey begins with us. Explore temples, holy sites, and sacred destinations across India.</p>
          <button
            onClick={() => {
              const toursSection = document.getElementById('tours-section');
              if (toursSection) {
                toursSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-white text-blue-600 px-6 sm:px-8 py-2 sm:py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            Explore Tours
          </button>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12">Why Choose Bidave Yatra?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="h-8 w-8 sm:h-10 sm:w-10 text-blue-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Expert Guidance</h3>
            <p className="text-gray-600 text-sm sm:text-base">Our experienced guides know the spiritual significance of every destination</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Group & Private Tours</h3>
            <p className="text-gray-600 text-sm sm:text-base">Flexible options for solo travelers, families, and groups</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 sm:h-10 sm:w-10 text-orange-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Flexible Payments</h3>
            <p className="text-gray-600 text-sm sm:text-base">Easy installment options to make your spiritual journey affordable</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">40+ Years of Journey</h3>
            <p className="text-gray-600 text-sm sm:text-base">Four decades of experience in spiritual tourism across India</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 sm:h-10 sm:w-10 text-red-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Home Like Meals</h3>
            <p className="text-gray-600 text-sm sm:text-base">Fresh, hygienic, and delicious meals prepared with love and care</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Luxurious Buses</h3>
            <p className="text-gray-600 text-sm sm:text-base">Comfortable, air-conditioned buses with modern amenities for your journey</p>
          </div>
        </div>
      </div>

      {/* Featured Tours */}
      <div id="tours-section" className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12">Featured Tours</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {tours.map((tour) => (
            <div key={tour.id} className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300 flex flex-col">
              <img src={tour.images[0]} alt={tour.name} className="w-full h-48 object-cover" />
              <div className="p-4 sm:p-6 flex-1 flex flex-col">
                <h3 className="text-lg sm:text-xl font-semibold mb-2">{tour.name}</h3>
                <p className="text-gray-600 mb-4 flex-1 text-sm sm:text-base">{tour.description.substring(0, 100)}...</p>
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{tour.duration}</span>
                </div>
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 mt-auto">
                  <button
                    onClick={() => {
                      setSelectedTour(tour);
                      setCurrentPage('tour-detail');
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors text-sm"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => {
                      setBookingFormData({...bookingFormData, tourName: tour.name});
                      setShowBookingForm(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sacred Destinations Gallery */}
      <div className="bg-gray-100 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12">Sacred Destinations</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {destinations.map((destination) => (
              <div 
                key={destination.id}
                onClick={() => {
                  setSelectedDestination(destination);
                  setCurrentPage('destination-detail');
                }}
                className="relative rounded-lg overflow-hidden shadow-lg cursor-pointer transform hover:scale-105 transition-transform duration-300"
              >
                <img 
                  src={destination.images[0]} 
                  alt={destination.name}
                  className="w-full h-48 sm:h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-lg sm:text-xl font-semibold">{destination.name}</h3>
                  <p className="text-sm">{destination.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-blue-900 text-white py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Contact Us</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            <div className="flex items-center justify-center space-x-3">
              <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
              <span className="text-sm sm:text-base">+91 9876543210</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
              <span className="text-sm sm:text-base">info@bidaveyatra.com</span>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />
              <span className="text-sm sm:text-base">Delhi, India</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Tour Detail Page
  const TourDetailPage = () => {
    if (!selectedTour) return null;

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
          <button
            onClick={() => setCurrentPage('home')}
            className="mb-4 sm:mb-6 text-blue-600 hover:underline text-sm sm:text-base"
          >
            ← Back to Home
          </button>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 sm:p-6">
              {selectedTour.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${selectedTour.name} ${index + 1}`}
                  className="w-full h-36 sm:h-48 object-cover rounded-lg"
                />
              ))}
            </div>

            <div className="p-4 sm:p-6">
              <h1 className="text-2xl sm:text-3xl font-bold mb-4">{selectedTour.name}</h1>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-3">Description</h3>
                  <p className="text-gray-600 mb-6 text-sm sm:text-base">{selectedTour.description}</p>

                  <h3 className="text-lg sm:text-xl font-semibold mb-3">Highlights</h3>
                  <ul className="list-disc pl-5 mb-6">
                    {selectedTour.highlights.map((highlight, index) => (
                      <li key={index} className="text-gray-600 text-sm sm:text-base">{highlight}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="bg-blue-50 p-4 sm:p-6 rounded-lg mb-6">
                    <h3 className="text-lg sm:text-xl font-semibold mb-4">Tour Details</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        <span className="text-sm sm:text-base">{selectedTour.duration}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="h-5 w-5 text-blue-600" />
                        <span className="text-sm sm:text-base">{selectedTour.location}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setBookingFormData({...bookingFormData, tourName: selectedTour.name});
                        setShowBookingForm(true);
                      }}
                      className="w-full mt-4 bg-green-600 text-white py-2 sm:py-3 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Book This Tour
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2 text-green-600">Inclusions</h4>
                      <ul className="text-sm space-y-1">
                        {selectedTour.inclusions.map((item, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <span className="text-green-600">✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2 text-red-600">Exclusions</h4>
                      <ul className="text-sm space-y-1">
                        {selectedTour.exclusions.map((item, index) => (
                          <li key={index} className="flex items-center space-x-2">
                            <span className="text-red-600">✗</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Destination Detail Page
  const DestinationDetailPage = () => {
    if (!selectedDestination) return null;

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
          <button
            onClick={() => setCurrentPage('home')}
            className="mb-4 sm:mb-6 text-blue-600 hover:underline text-sm sm:text-base"
          >
            ← Back to Home
          </button>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:p-6">
              {selectedDestination.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`${selectedDestination.name} ${index + 1}`}
                  className="w-full h-48 sm:h-64 object-cover rounded-lg"
                />
              ))}
            </div>

            <div className="p-4 sm:p-6">
              <h1 className="text-2xl sm:text-3xl font-bold mb-4">{selectedDestination.name}</h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6">{selectedDestination.location}</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-3">About</h3>
                  <p className="text-gray-600 mb-6 text-sm sm:text-base">{selectedDestination.description}</p>

                  <h3 className="text-lg sm:text-xl font-semibold mb-3">Best Time to Visit</h3>
                  <p className="text-gray-600 mb-6 text-sm sm:text-base">{selectedDestination.bestTimeToVisit}</p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-3">History</h3>
                  <p className="text-gray-600 mb-6 text-sm sm:text-base">{selectedDestination.history}</p>

                  <h3 className="text-lg sm:text-xl font-semibold mb-3">Nearby Attractions</h3>
                  <ul className="list-disc pl-5">
                    {selectedDestination.nearbyAttractions.map((attraction, index) => (
                      <li key={index} className="text-gray-600 text-sm sm:text-base">{attraction}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Admin Dashboard
  const AdminDashboard = () => {
    if (!isLoggedIn) {
      setCurrentPage('login');
      return null;
    }

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Admin Dashboard</h1>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
              <h3 className="text-xs font-medium text-gray-500 uppercase">Total Tours</h3>
              <p className="text-lg sm:text-xl font-bold text-blue-600">{tours.length}</p>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
              <h3 className="text-xs font-medium text-gray-500 uppercase">Destinations</h3>
              <p className="text-lg sm:text-xl font-bold text-purple-600">{destinations.length}</p>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
              <h3 className="text-xs font-medium text-gray-500 uppercase">Outstanding</h3>
              <p className="text-lg sm:text-xl font-bold text-red-600">₹{totalOutstanding.toLocaleString()}</p>
            </div>
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow">
              <h3 className="text-xs font-medium text-gray-500 uppercase">Collected</h3>
              <p className="text-lg sm:text-xl font-bold text-green-600">₹{totalCollected.toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6 sm:mb-8">
            <div className="p-4 sm:p-6 border-b">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-lg sm:text-xl font-semibold">Tours Management</h2>
                <button
                  onClick={() => setShowAddTour(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Tour</span>
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tour Name</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Duration</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Location</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tours.map((tour) => (
                    <tr key={tour.id}>
                      <td className="px-4 sm:px-6 py-4 text-sm font-medium">{tour.name}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">₹{tour.price.toLocaleString()}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm hidden sm:table-cell">{tour.duration}</td>
                      <td className="px-4 sm:px-6 py-4 text-sm hidden lg:table-cell">{tour.location}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingTour(tour)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteTour(tour.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6 sm:mb-8">
            <div className="p-4 sm:p-6 border-b">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-lg sm:text-xl font-semibold">Destinations Management</h2>
                <button
                  onClick={() => setShowAddDestination(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center justify-center space-x-2 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Destination</span>
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Best Time</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {destinations.map((destination) => (
                    <tr key={destination.id}>
                      <td className="px-4 sm:px-6 py-4 text-sm font-medium">{destination.name}</td>
                      <td className="px-4 sm:px-6 py-4 text-sm">{destination.location}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm hidden sm:table-cell">{destination.bestTimeToVisit}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingDestination(destination)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteDestination(destination.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Customer Management Page
  const CustomerManagement = () => {
    if (!isLoggedIn) {
      setCurrentPage('login');
      return null;
    }

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Customer Management</h1>

          <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                onClick={() => setShowAddCustomer(true)}
                className="bg-green-600 text-white px-4 sm:px-6 py-2 rounded hover:bg-green-700 flex items-center justify-center space-x-2 whitespace-nowrap text-sm"
              >
                <Plus className="h-4 w-4" />
                <span>Add Customer</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Tour</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id}>
                      <td className="px-4 sm:px-6 py-4">
                        <div>
                          <div className="font-medium text-sm">{customer.name}</div>
                          <div className="text-gray-500 text-xs">{customer.phone}</div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm hidden sm:table-cell">{customer.tourName}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">₹{customer.totalAmount.toLocaleString()}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">₹{customer.paidAmount.toLocaleString()}</td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          customer.remainingBalance === 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          ₹{customer.remainingBalance.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingCustomer(customer)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setShowAddPayment(true);
                            }}
                            className="text-green-600 hover:text-green-800"
                          >
                            <CreditCard className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setCurrentPage('customer-detail');
                            }}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <User className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Customer Detail Page
  const CustomerDetailPage = () => {
    if (!selectedCustomer) return null;

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
          <button
            onClick={() => setCurrentPage('customers')}
            className="mb-4 sm:mb-6 text-blue-600 hover:underline text-sm sm:text-base"
          >
            ← Back to Customers
          </button>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 sm:p-6 border-b">
              <h1 className="text-xl sm:text-2xl font-bold">{selectedCustomer.name}</h1>
              <p className="text-gray-600 text-sm sm:text-base">{selectedCustomer.email} | {selectedCustomer.phone}</p>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Customer Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="font-medium text-sm">Address:</label>
                      <p className="text-gray-600 text-sm">{selectedCustomer.address}</p>
                    </div>
                    <div>
                      <label className="font-medium text-sm">Tour:</label>
                      <p className="text-gray-600 text-sm">{selectedCustomer.tourName}</p>
                    </div>
                    <div>
                      <label className="font-medium text-sm">Booking Date:</label>
                      <p className="text-gray-600 text-sm">{selectedCustomer.bookingDate}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Payment Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm sm:text-base">
                      <span>Total Amount:</span>
                      <span className="font-semibold">₹{selectedCustomer.totalAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base">
                      <span>Paid Amount:</span>
                      <span className="font-semibold text-green-600">₹{selectedCustomer.paidAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base">
                      <span>Remaining Balance:</span>
                      <span className="font-semibold text-red-600">₹{selectedCustomer.remainingBalance.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Payment History</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCustomer.payments.map((payment) => (
                        <tr key={payment.id} className="border-t">
                          <td className="px-3 sm:px-4 py-2 text-sm">{payment.date}</td>
                          <td className="px-3 sm:px-4 py-2 text-sm">₹{payment.amount.toLocaleString()}</td>
                          <td className="px-3 sm:px-4 py-2 text-sm">{payment.method}</td>
                          <td className="px-3 sm:px-4 py-2">
                            <button 
                              onClick={() => downloadReceipt(payment, selectedCustomer)}
                              className="text-blue-600 hover:underline flex items-center space-x-1 text-sm"
                            >
                              <Download className="h-4 w-4" />
                              <span>Download</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Booking Form Modal
  const BookingForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Book Your Tour</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <input
                type="text"
                value={bookingFormData.name}
                onChange={(e) => setBookingFormData({...bookingFormData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <input
                type="email"
                value={bookingFormData.email}
                onChange={(e) => setBookingFormData({...bookingFormData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Phone *</label>
              <input
                type="tel"
                value={bookingFormData.phone}
                onChange={(e) => setBookingFormData({...bookingFormData, phone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tour *</label>
              <select
                value={bookingFormData.tourName}
                onChange={(e) => setBookingFormData({...bookingFormData, tourName: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              >
                <option value="">Select a tour</option>
                {tours.map(tour => (
                  <option key={tour.id} value={tour.name}>{tour.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Number of People *</label>
              <input
                type="number"
                min="1"
                value={bookingFormData.numberOfPeople}
                onChange={(e) => setBookingFormData({...bookingFormData, numberOfPeople: Number(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                type="button"
                onClick={() => setShowBookingForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitBookingForm}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              >
                Submit Booking
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Tour Form Modal
  const TourForm = ({ tour, onSave, onCancel }: { tour?: Tour | null; onSave: (data: any) => void; onCancel: () => void }) => {
    const [formData, setFormData] = useState({
      name: tour?.name || '',
      description: tour?.description || '',
      price: tour?.price || 0,
      duration: tour?.duration || '',
      location: tour?.location || '',
      images: tour?.images?.join(', ') || '',
      highlights: tour?.highlights?.join(', ') || '',
      inclusions: tour?.inclusions?.join(', ') || '',
      exclusions: tour?.exclusions?.join(', ') || ''
    });

    const handleSubmit = () => {
      if (!formData.name || !formData.description || !formData.price || !formData.duration || !formData.location) {
        alert('Please fill in all required fields');
        return;
      }
      
      onSave({
        ...formData,
        price: Number(formData.price),
        images: formData.images.split(',').map(img => img.trim()).filter(img => img),
        highlights: formData.highlights.split(',').map(h => h.trim()).filter(h => h),
        inclusions: formData.inclusions.split(',').map(i => i.trim()).filter(i => i),
        exclusions: formData.exclusions.split(',').map(e => e.trim()).filter(e => e)
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{tour ? 'Edit Tour' : 'Add New Tour'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Tour Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={3}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Price (₹) *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Duration *</label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                    placeholder="e.g., 5 Days / 4 Nights"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Images (comma-separated URLs)</label>
                <input
                  type="text"
                  value={formData.images}
                  onChange={(e) => setFormData({...formData, images: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="image1.jpg, image2.jpg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Highlights (comma-separated)</label>
                <input
                  type="text"
                  value={formData.highlights}
                  onChange={(e) => setFormData({...formData, highlights: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Highlight 1, Highlight 2, Highlight 3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Inclusions (comma-separated)</label>
                <input
                  type="text"
                  value={formData.inclusions}
                  onChange={(e) => setFormData({...formData, inclusions: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Inclusion 1, Inclusion 2, Inclusion 3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Exclusions (comma-separated)</label>
                <input
                  type="text"
                  value={formData.exclusions}
                  onChange={(e) => setFormData({...formData, exclusions: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Exclusion 1, Exclusion 2, Exclusion 3"
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  {tour ? 'Update' : 'Add'} Tour
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Destination Form Modal
  const DestinationForm = ({ destination, onSave, onCancel }: { destination?: Destination | null; onSave: (data: any) => void; onCancel: () => void }) => {
    const [formData, setFormData] = useState({
      name: destination?.name || '',
      location: destination?.location || '',
      description: destination?.description || '',
      history: destination?.history || '',
      images: destination?.images?.join(', ') || '',
      bestTimeToVisit: destination?.bestTimeToVisit || '',
      nearbyAttractions: destination?.nearbyAttractions?.join(', ') || ''
    });

    const handleSubmit = () => {
      if (!formData.name || !formData.location || !formData.description || !formData.history) {
        alert('Please fill in all required fields');
        return;
      }
      
      onSave({
        ...formData,
        images: formData.images.split(',').map(img => img.trim()).filter(img => img),
        nearbyAttractions: formData.nearbyAttractions.split(',').map(a => a.trim()).filter(a => a)
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{destination ? 'Edit Destination' : 'Add New Destination'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">History *</label>
                <textarea
                  value={formData.history}
                  onChange={(e) => setFormData({...formData, history: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Best Time to Visit</label>
                <input
                  type="text"
                  value={formData.bestTimeToVisit}
                  onChange={(e) => setFormData({...formData, bestTimeToVisit: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g., October to March"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Images (comma-separated URLs)</label>
                <input
                  type="text"
                  value={formData.images}
                  onChange={(e) => setFormData({...formData, images: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="image1.jpg, image2.jpg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Nearby Attractions (comma-separated)</label>
                <input
                  type="text"
                  value={formData.nearbyAttractions}
                  onChange={(e) => setFormData({...formData, nearbyAttractions: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Attraction 1, Attraction 2, Attraction 3"
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                >
                  {destination ? 'Update' : 'Add'} Destination
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Customer Form Modal
  const CustomerForm = ({ customer, onSave, onCancel }: { customer?: Customer | null; onSave: (data: any) => void; onCancel: () => void }) => {
    const [formData, setFormData] = useState({
      name: customer?.name || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
      address: customer?.address || '',
      tourId: customer?.tourId || '',
      totalAmount: customer?.totalAmount || 0,
      paidAmount: customer?.paidAmount || 0,
      bookingDate: customer?.bookingDate || new Date().toISOString().split('T')[0]
    });

    const handleSubmit = () => {
      if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.tourId || !formData.totalAmount) {
        alert('Please fill in all required fields');
        return;
      }
      
      const selectedTour = tours.find(t => t.id === formData.tourId);
      onSave({
        ...formData,
        totalAmount: Number(formData.totalAmount),
        paidAmount: Number(formData.paidAmount),
        tourName: selectedTour?.name || ''
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{customer ? 'Edit Customer' : 'Add New Customer'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Address *</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={2}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tour *</label>
                <select
                  value={formData.tourId}
                  onChange={(e) => setFormData({...formData, tourId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="">Select a tour</option>
                  {tours.map(tour => (
                    <option key={tour.id} value={tour.id}>{tour.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Total Amount (₹) *</label>
                  <input
                    type="number"
                    value={formData.totalAmount}
                    onChange={(e) => setFormData({...formData, totalAmount: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Paid Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.paidAmount}
                    onChange={(e) => setFormData({...formData, paidAmount: Number(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Booking Date *</label>
                  <input
                    type="date"
                    value={formData.bookingDate}
                    onChange={(e) => setFormData({...formData, bookingDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  {customer ? 'Update' : 'Add'} Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Payment Form Modal
  const PaymentForm = ({ customer, onSave, onCancel }: { customer: Customer; onSave: (amount: number, method: string) => void; onCancel: () => void }) => {
    const [amount, setAmount] = useState(0);
    const [method, setMethod] = useState('Cash');

    const handleSubmit = () => {
      if (!amount || amount <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      
      if (amount > customer.remainingBalance) {
        alert('Payment amount cannot exceed remaining balance');
        return;
      }
      
      onSave(amount, method);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-md w-full">
          <div className="p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Add Payment</h2>
            <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-sm sm:text-base">{customer.name}</h3>
              <p className="text-xs sm:text-sm text-gray-600">Remaining Balance: ₹{customer.remainingBalance.toLocaleString()}</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                  min="1"
                  max={customer.remainingBalance}
                  placeholder={`Max: ₹${customer.remainingBalance.toLocaleString()}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Payment Method</label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                </select>
              </div>
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm"
                >
                  <Send className="h-4 w-4" />
                  <span>Add Payment</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'login' && <LoginPage />}
      {currentPage === 'tour-detail' && <TourDetailPage />}
      {currentPage === 'destination-detail' && <DestinationDetailPage />}
      {currentPage === 'admin' && <AdminDashboard />}
      {currentPage === 'customers' && <CustomerManagement />}
      {currentPage === 'customer-detail' && <CustomerDetailPage />}

      {/* Modals */}
      {showBookingForm && <BookingForm />}

      {showAddTour && (
        <TourForm
          onSave={addTour}
          onCancel={() => setShowAddTour(false)}
        />
      )}

      {editingTour && (
        <TourForm
          tour={editingTour}
          onSave={(data) => updateTour(editingTour.id, data)}
          onCancel={() => setEditingTour(null)}
        />
      )}

      {showAddDestination && (
        <DestinationForm
          onSave={addDestination}
          onCancel={() => setShowAddDestination(false)}
        />
      )}

      {editingDestination && (
        <DestinationForm
          destination={editingDestination}
          onSave={(data) => updateDestination(editingDestination.id, data)}
          onCancel={() => setEditingDestination(null)}
        />
      )}

      {showAddCustomer && (
        <CustomerForm
          onSave={addCustomer}
          onCancel={() => setShowAddCustomer(false)}
        />
      )}

      {editingCustomer && (
        <CustomerForm
          customer={editingCustomer}
          onSave={(data) => updateCustomer(editingCustomer.id, data)}
          onCancel={() => setEditingCustomer(null)}
        />
      )}

      {showAddPayment && selectedCustomer && (
        <PaymentForm
          customer={selectedCustomer}
          onSave={(amount, method) => addPayment(selectedCustomer.id, amount, method)}
          onCancel={() => {
            setShowAddPayment(false);
            setSelectedCustomer(null);
          }}
        />
      )}
    </div>
  );
};

export default BidaweTravel;